import type * as ngRouter from '@uirouter/angularjs';
import { Observable } from 'rxjs';
import { withLatestFrom, filter, map, take } from 'rxjs/operators';

import { Actions } from '~/actions';
import { ExplorationViewDataService } from '~/modules/report-viewer/services/exploration-view-data.service';
import { ofType } from '@mode/legacy/util-domain';
import { AppStore } from '~/types/main.types';

import { ExplorerActionType } from '../actions/chart-designer/explorer.actions';
import { RouterActionType } from '../actions/router-actions';
import { routerSelector, selectedReportDisplayNameSelector } from '../reducers/selectors';
import { isExplorerRoute, isExplorationRoute, isDatasetsRoute, isReportViewerRoute } from '../lib/router-helpers';
import { TempActions, ChartActions } from '@mode/charts/domain';
import { ExplorerSelectors } from '~/selectors/explorer.selectors';
import { ChartExplorerService } from './chart-designer/chart-explorer.service';
import angular from 'angular';
import { getAppName, isPresent } from '@mode/shared/util-js';
import { ReportFacade } from '@mode/reports/domain';
import { DataViewFacade } from '@mode/switch-views/contract';
import { EnhancedDatasetResult } from '@mode/shared/contract-common';

export class RouterStoreService {
  static $inject = [
    '$state',
    '$transitions',
    'RxStoreService',
    'actions$',
    'ExplorationViewDataService',
    'ChartExplorerService',
    'ReportFacade',
    'DataViewFacade',
  ];
  readonly appName = getAppName();

  constructor(
    private $state: ngRouter.StateService,
    $transitions: ngRouter.Transition,
    private _storeService: AppStore,
    private actions$: Observable<Actions>,
    private explorationViewDataService: ExplorationViewDataService,
    private explorerService: ChartExplorerService, // for effects
    private reportFacade: ReportFacade,
    private dataViewFacade: DataViewFacade
  ) {
    this.updateRouterState($state.$current, $state.params);

    $transitions.onSuccess({}, (trans) => {
      this.updateRouterState(trans.to(), trans.params('to'));

      /**
       * TODO: This method of setting the browser title is transitional.
       * Please see GitHub issue:
       */
      if (isExplorationRoute($state)) {
        this._storeService
          .selectNoEmpty(ExplorerSelectors.title)
          .subscribe((name) => (window.document.title = `${name} | ${this.appName}`));
      } else {
        this.reportFacade.displayNameForHeader$.subscribe((name) => {
          const title = name || (this.isDatasetsRoute() ? 'Untitled Dataset' : 'Untitled Report');
          window.document.title = `${title} | ${this.appName}`;
        });
      }
    });

    /**
     * Explore button clicked on report view
     */
    this.actions$.pipe(ofType(ExplorerActionType.ExploreChart)).subscribe((action) => {
      $state.go(this.routerStateWithRun(), { vizId: action.chartToken });
    });

    /**
     * Explore was clicked from context menu on specific point/mark in visualization
     */
    this.actions$.pipe(ofType(TempActions.Type.ExploreChartMark)).subscribe((action) => {
      $state.go(this.routerStateWithRun(), { vizId: action.chartToken });
    });

    /**
     * This is needed for the explorer to load when navigating directly to the page
     * TODO: Remove this
     *
     */
    this.actions$
      .pipe(
        ofType(ChartActions.loadChartsSuccess),
        withLatestFrom(_storeService.select(routerSelector)),
        // Only fire if we're loading the query result associated with the chart that is being explored, or on a viz only page
        filter(
          ([action, routerState]) =>
            isExplorerRoute(routerState.name) &&
            action.payload.findIndex((chart) => chart.token === routerState.params['vizId']) >= 0
        )
      )
      .subscribe(([action, routerState]) => {
        _storeService.dispatch({
          type: ExplorerActionType.ExploreChartOnLoad,
          chartToken: routerState.params['vizId'],
        });
      });

    /**
     * Pop save modal if user attempts to exit `/explore` with in memory changes and has not saved
     */
    $transitions.onStart({}, (trans) => {
      const state = trans.to();
      const params = trans.params('to');
      const oldState = trans.from();
      this.showSaveExplorationModal(state, params, oldState);
    });
  }

  showSaveExplorationModal(state, params, oldState) {
    const switchViewToken = this.explorationViewDataService.switchViewToken;
    const datasetResults$ = this.dataViewFacade.createEnhancedDatasetResultBySwitchViewToken(switchViewToken);
    datasetResults$.pipe(take(1)).subscribe((results: EnhancedDatasetResult) => {
      if (this.$state.includes('**.explore') && oldState.data.hasChanges && results.isLocal) {
        this.explorationViewDataService.openSaveExplorationModal(
          'Do you want to save this Exploration?',
          'You can choose to save your work, or discard your changes.',
          'Discard',
          { name: state.name, params, routeData: oldState.data }
        );
      }
    });
  }

  /**
   * Helper that directly returns an observable that provides the param value for a
   * matching param and state name combination for any given active route state change.
   * Providing a way to share an active route param value to new Angular components.
   */
  createActiveRouteObservable(stateName: string, param: string) {
    return this.store.selectNoEmpty(routerSelector).pipe(
      map((route) => {
        if (route.name === stateName) {
          return !angular.isUndefined(route.params[param]) ? route.params[param] : '';
        }

        return '';
      })
    );
  }

  /**
   * Helper that creates an observable of the route state.
   */
  createRouteStateObservable() {
    return this.store.selectNoEmpty(routerSelector).pipe(filter(isPresent));
  }

  routerStateWithRun(): string {
    return this.$state.params.runId ? 'reportShow.runs.chart.explore' : 'reportShow.noRuns.chart.explore';
  }

  editorRoute(): boolean {
    return this.$state.includes('**.reports.edit') || this.$state.includes('**.reports.edit.**');
  }

  isDatasetsRoute(): boolean {
    return isDatasetsRoute(this.$state);
  }

  reportViewRoute(): boolean {
    return this.$state.includes('reportShow.noRuns') || this.$state.includes('reportShow.runs');
  }

  reportShowExploreUrl(vizToken: string): string {
    let { ownerId, reportId } = this.$state.params;
    /**
     * TODO: Replace with return this.$state.href(routerStateWithRun(), {vizId: vizToken})
     * When Report routes and Edit routes are unified
     */
    return `/${ownerId}/reports/${reportId}/viz/${vizToken}/explore`;
  }

  queryResultUrl(queryToken: string): string {
    let reportId = this.$state.params.reportId;
    let ownerId = this.$state.params.ownerId || this.$state.params.account;

    /**
     * TODO: Replace with return this.$state.href('application.reports.edit.queries.edit', {queryToken})
     * When Report routes and Edit routes are unified
     */
    return `/editor/${ownerId}/reports/${reportId}/queries/${queryToken}`;
  }

  datasetResultUrl(datasetToken: string): string {
    const reportId = this.$state.params.reportId;
    const ownerId = this.$state.params.ownerId || this.$state.params.account;

    return `/editor/${ownerId}/reports/${reportId}/datasets/${datasetToken}`;
  }

  private get store(): AppStore {
    return this._storeService;
  }

  updateRouterState(state: any, params: any): void {
    this.store.dispatch({
      type: RouterActionType.UpdateRoute,
      payload: { name: state.name, url: state.url, params: params },
    });
  }
}
